import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, FlaskConical, CheckCircle2, XCircle, Play, AlertCircle } from 'lucide-react';
import { adminKbRunSelfTest, adminKbSelfTestStatus, adminKbSelfTestHistory, type KbSelfTestRun } from '../../services/adminService';

const input = 'w-32 rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-sm focus:outline-none focus:border-secondary-500';

/** One self-test step as a pass/fail row. */
const StepRow: React.FC<{ name: string; ok: boolean; detail: string; ms: number }> = ({ name, ok, detail, ms }) => (
  <li className="flex items-start gap-2 py-1">
    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />}
    <div className="min-w-0">
      <span className="text-sm text-secondary-900 dark:text-white">{name}</span>
      <span className="ml-2 text-xs text-secondary-400 tabular-nums">{ms}ms</span>
      {detail && <p className="text-xs text-secondary-500 break-words">{detail}</p>}
    </div>
  </li>
);

/**
 * Admin diagnostics: run the KB-learning self-test on real infra (classifier, source registry, learn → publish →
 * archive, retrieval, scheduler) and show the per-step report. Server-side, so it needs no local setup — one
 * click validates the whole loop. The run is logged (kb_selftest_runs) as an audit record.
 */
const SelfTestSection: React.FC<{ domains?: string[] }> = ({ domains = [] }) => {
  const [run, setRun] = useState<KbSelfTestRun | null>(null);
  const [domain, setDomain] = useState('markets');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => { adminKbSelfTestHistory().then((r) => { if (aliveRef.current) setRun(r[0] || null); }).catch(() => undefined); }, []);

  const runNow = useCallback(() => {
    setBusy(true); setError('');
    adminKbRunSelfTest(domain.trim() || undefined).then(({ runId }) => {
      const poll = () => adminKbSelfTestStatus(runId).then((r) => {
        if (!aliveRef.current) return;
        setRun(r);
        if (r.status === 'running') { window.setTimeout(poll, 2500); return; }
        if (r.status === 'error') setError(r.error || 'Self-test failed to run');
        setBusy(false);
      }).catch((e: any) => { if (aliveRef.current) { setError(e?.message || 'Failed to poll self-test'); setBusy(false); } });
      poll();
    }).catch((e: any) => { setError(e?.message || 'Failed to start self-test'); setBusy(false); });
  }, [domain]);

  const steps = run?.steps || [];
  const passed = steps.filter((s) => s.ok).length;

  return (
    <div className="mb-5 rounded-xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-secondary-500" />
          <span className="font-semibold text-secondary-900 dark:text-white">Self-test</span>
          <span className="text-xs text-secondary-500">validate the learning loop on real infrastructure</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Pick the real domain for the read-only retrieval probe — no typing a reserved/typo'd id. */}
          <select value={domain} onChange={(e) => setDomain(e.target.value)} className={input} disabled={busy} aria-label="Retrieval-probe domain">
            {(domains.length ? domains : ['markets']).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button onClick={runNow} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 text-white dark:bg-white dark:text-secondary-900 px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {busy ? 'Running…' : 'Run self-test'}
          </button>
        </div>
      </div>

      {error && <div className="mt-3 flex items-center gap-2 rounded-lg bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-600"><AlertCircle className="w-4 h-4 shrink-0" />{error}</div>}

      {run && (
        <div className="mt-3">
          {steps.length > 0 && (
            <div className={`text-sm font-medium ${run.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
              {passed}/{steps.length} checks passed{run.status === 'running' ? ' (running…)' : ''}
            </div>
          )}
          <ul className="mt-1 divide-y divide-secondary-100 dark:divide-secondary-800">
            {steps.map((s, i) => <StepRow key={i} {...s} />)}
          </ul>
          {run.status === 'running' && steps.length === 0 && <div className="text-sm text-secondary-500">Running the loop on real infra (~30s)…</div>}
        </div>
      )}
    </div>
  );
};

export default SelfTestSection;
